import React, { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import {
  InputContainer,
  NumberInput,
  FaChevronUpIcon,
  FaChevronDownIcon,
} from './time-amount-picker.styles';

const TimeAmountPicker = ({
  onChange = () => {},
  defaultValue,
  ...otherProps
}) => {
  const [hours, setHours] = useState(
    defaultValue ? Number(defaultValue.split(':')[0]) : 0
  );
  const [minutes, setMinutes] = useState(
    defaultValue ? Number(defaultValue.split(':')[1]) : 0
  );
  const [holding, setHolding] = useState(null);
  const [timerConfig, setTimerConfig] = useState({
    iterations: 0,
    speed: 300,
  });

  const isHoursValid = (value) => !isNaN(value) && value >= 0 && value <= 23;
  const isMinutesValid = (value) => !isNaN(value) && value >= 0 && value <= 59;

  useEffect(() => {
    onChange(`${hours}:${minutes}`);
  }, [hours, minutes]);

  useEffect(() => {
    if (holding) {
      const { type, direction } = holding;

      let timer = null;

      const timerFunction = () => {
        if (timerConfig.iterations > 10) {
          setTimerConfig(({ iterations }) => ({
            iterations: iterations + 1,
            speed: 100,
          }));
          clearTimeout(timer);
          timer = setTimeout(timerFunction, timerConfig.speed);
        }

        switch (direction) {
          case 'up':
            handleUp(type, type === 'hours' ? hours : minutes);
            break;
          case 'down':
            handleDown(type, type === 'hours' ? hours : minutes);
            break;
          default:
            break;
        }

        setTimerConfig(({ iterations, speed }) => ({
          iterations: iterations + 1,
          speed,
        }));
      };

      timer = setTimeout(timerFunction, timerConfig.speed);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [holding, hours, minutes, timerConfig]);

  const handleHoursChange = (e) => {
    try {
      const value = Number(e.target.value);
      if (!isHoursValid(value)) {
        return;
      }

      setHours(value);
    } catch (e) {}
  };

  const handleMinutesChange = (e) => {
    try {
      const value = Number(e.target.value);
      if (!isMinutesValid(value)) {
        return;
      }

      setMinutes(value);
    } catch (e) {}
  };

  const handleUp = (type, prevAmount = 0) => {
    switch (type) {
      case 'hours':
        if (prevAmount >= 23) {
          setHours(0);
          return false;
        }

        setHours(prevAmount + 1);
        break;
      case 'minutes':
        if (prevAmount >= 59) {
          setMinutes(0);
          return false;
        }

        setMinutes(prevAmount + 1);
        break;
      default:
        return false;
    }

    return true;
  };

  const handleDown = (type, prevAmount = 0) => {
    switch (type) {
      case 'hours':
        if (prevAmount <= 0) {
          setHours(23);
          return false;
        }

        setHours(prevAmount - 1);
        break;
      case 'minutes':
        if (prevAmount <= 0) {
          setMinutes(59);
          return false;
        }
        
        setMinutes(prevAmount - 1);
        break;
      default:
        return false;
    }

    return true;
  };

  const handleReset = () => {
    setHolding(null);
    setTimerConfig({ iterations: 0, speed: 300 });
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '0.2rem',
        alignItems: 'center',
        ...otherProps,
      }}
    >
      <InputContainer>
        <FaChevronUpIcon
          onMouseDown={() => setHolding({ type: 'hours', direction: 'up' })}
          onMouseUp={handleReset}
          onClick={() => handleUp('hours', hours)}
        />
        <NumberInput
          type={'text'}
          maxLength={2}
          onChange={handleHoursChange}
          value={hours}
        />
        <FaChevronDownIcon
          onMouseDown={() => setHolding({ type: 'hours', direction: 'down' })}
          onMouseUp={handleReset}
          onClick={() => handleDown('hours', hours)}
        />
      </InputContainer>
      <Form.Label style={{ fontWeight: '500', userSelect: 'none' }}>
        :
      </Form.Label>
      <InputContainer>
        <FaChevronUpIcon
          onMouseDown={() => setHolding({ type: 'minutes', direction: 'up' })}
          onMouseUp={handleReset}
          onClick={() => handleUp('minutes', minutes)}
        />
        <NumberInput
          type={'text'}
          maxLength={2}
          onChange={handleMinutesChange}
          value={minutes}
        />
        <FaChevronDownIcon
          onMouseDown={() => setHolding({ type: 'minutes', direction: 'down' })}
          onMouseUp={handleReset}
          onClick={() => handleDown('minutes', minutes)}
        />
      </InputContainer>
    </div>
  );
};

export default TimeAmountPicker;
